import { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaFileDownload, FaFilter } from 'react-icons/fa';
import { FiFileText } from 'react-icons/fi';
import { MdDelete, MdMoreHoriz } from 'react-icons/md';
import { Link } from 'react-router-dom';
import AuthenticateContext from '../../provider/context/authenticate.context';
import { showSuccessMessage } from '../../module/message';
import {
  getSalesCenterList,
  batchDeleteSalesCenter,
  deleteSalesCenter,
  exportSalesCenters,
} from '../../module/salesCenter';
import { getBottlerSimpleList } from '../../module/bottler';
import { downloadFile } from '../../module/download';
import handleApiResponse from '../../utils/api/handleApiResponse';
import {
  Section,
  SectionBody,
  SectionHeader,
  SectionToolbar,
  SectionToolbarItem,
} from '../../components/section';
import { Heading1 } from '../../components/heading';
import { Button, IconButton, ButtonGroup } from '../../components/button';
import Dropdown, {
  DropdownItem,
  DropdownButtonOption,
  DropdownLinkOption,
} from '../../components/dropdown';
import { Table } from '../../components/table';
import { useLimitChange, usePageChange } from '../../components/table/hooks';
import { StateModal, TitleModal } from '../../components/modal';
import { Grid, Column } from '../../components/grid';
import { FormItem, Input, Label, Checkbox } from '../../components/form';
import { Select } from '../../components/Select';

const SalesCenterList = () => {
  const { t: trans } = useTranslation();
  const { userInformation } = useContext(AuthenticateContext);
  const [selectedSalesCenter, setSelectedSalesCenter] = useState([]);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [listData, setListData] = useState([]);
  const [listParams, setListParams] = useState({
    page: 1,
    limit: 10,
    filters: {
      name: '',
      bottlerId: [],
      description: '',
    },
  });
  const [total, setTotal] = useState(1);
  const [bottlerList, setBottlerList] = useState({
    data: [],
    currentPage: 1,
    lastPage: 1,
  });
  const [bottlerSimpleList, setBottlerSimpleList] = useState([]);

  const onLimitChange = useLimitChange(setListParams);
  const onPageChange = usePageChange(setListParams);

  const handleGetBottlerList = useCallback((currentPage) => {
    const parameters = {
      page: currentPage,
      limit: 100,
    };
    handleApiResponse(getBottlerSimpleList(parameters), (response) => {
      const { list, total } = response.data.data;

      setBottlerList((previous) => {
        const newBottlerList = [...previous.data, ...list];

        return {
          data: newBottlerList,
          currentPage: currentPage + 1,
          lastPage: Math.ceil(total / 100),
        };
      });
    });
  }, []);

  const handleSelectedChange = (id) => {
    setSelectedSalesCenter((previous) => {
      const newSelectedSalesCenter = [...previous];
      const index = previous.indexOf(id);
      if (index === -1) {
        newSelectedSalesCenter.push(id);
      } else {
        newSelectedSalesCenter.splice(index, 1);
      }

      return newSelectedSalesCenter;
    });
  };

  const handleSelectAll = useCallback(() => {
    if (isSelectedAll) {
      setSelectedSalesCenter([]);
    } else {
      const newSelectedSalesCenter = [];
      listData.forEach((bottlerGroup) => {
        newSelectedSalesCenter.push(bottlerGroup.id);
      });
      setSelectedSalesCenter(newSelectedSalesCenter);
    }

    setIsSelectedAll(!isSelectedAll);
  }, [isSelectedAll, listData]);

  const handleGetSalesCenterList = useCallback(() => {
    const requestData = {
      page: listParams.page,
      limit: listParams.limit,
    };
    if (listParams.filters.name.trim()) {
      requestData.name = listParams.filters.name.trim();
    }
    if (listParams.filters.bottlerId.length > 0) {
      requestData.bottlerId = listParams.filters.bottlerId.join(',');
    }
    if (listParams.filters.description.trim()) {
      requestData.description = listParams.filters.description.trim();
    }

    handleApiResponse(getSalesCenterList(requestData), (response) => {
      const { total, list } = response.data.data;
      setTotal(total);
      setListData(list);
      setSelectedSalesCenter([]);
    });
  }, [listParams]);

  const handleBatchDeleteSalesCenter = (ids) => {
    handleApiResponse(batchDeleteSalesCenter(ids), () => {
      showSuccessMessage({ message: trans('success:RemoveSalesCenter') });
      handleGetSalesCenterList();
    });
  };

  const handleDeleteSalesCenter = useCallback(
    (id) => {
      handleApiResponse(deleteSalesCenter(id), () => {
        showSuccessMessage({ message: trans('success:RemoveSalesCenter') });
        handleGetSalesCenterList();
      });
    },
    [trans, handleGetSalesCenterList],
  );

  const handleBatchRemove = (ids) => {
    return StateModal({
      type: 'warning',
      title: trans('AreYouSure'),
      text: trans('DoYouWantToRemoveTheseSalesCenters'),
      showCancelButton: true,
      confirmButtonText: trans('button:Confirm'),
      cancelButtonText: trans('button:Cancel'),
      allowDismiss: true,
      onConfirm() {
        handleBatchDeleteSalesCenter(ids);
      },
    });
  };

  const handleRemove = useCallback(
    (id) => {
      return StateModal({
        type: 'warning',
        title: trans('AreYouSure'),
        text: trans('DoYouWantToRemoveThisSalesCenter'),
        showCancelButton: true,
        confirmButtonText: trans('button:Confirm'),
        cancelButtonText: trans('button:Cancel'),
        allowDismiss: true,
        onConfirm() {
          handleDeleteSalesCenter(id);
        },
      });
    },
    [trans, handleDeleteSalesCenter],
  );

  const generateActionDropdown = useCallback(
    ({ id }) => {
      return (
        <Dropdown>
          <Dropdown.Toggle>
            <IconButton>
              <MdMoreHoriz />
            </IconButton>
          </Dropdown.Toggle>
          <Dropdown.Content>
            <DropdownItem>
              <DropdownLinkOption to={`/sales-center/${id}`}>
                <FiFileText />
                {trans('button:Details')}
              </DropdownLinkOption>
            </DropdownItem>
            {userInformation.permissions.includes('sales_center.delete') && (
              <DropdownItem onClick={() => handleRemove(id)}>
                <DropdownButtonOption>
                  <MdDelete />
                  {trans('button:Remove')}
                </DropdownButtonOption>
              </DropdownItem>
            )}
          </Dropdown.Content>
        </Dropdown>
      );
    },
    [trans, userInformation.permissions, handleRemove],
  );

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: trans('Name'),
        fieldName: 'name',
      },
      {
        title: trans('Bottler'),
        fieldName: 'bottlerName',
      },
      {
        title: trans('TerminalCount'),
        fieldName: 'terminalCount',
      },
      {
        title: trans('Description'),
        fieldName: 'description',
      },
      {
        title: trans('Action'),
        custom: true,
        width: '10%',
        align: 'center',
        render(data) {
          return generateActionDropdown(data);
        },
      },
    ];

    if (userInformation.permissions.includes('sales_center.delete')) {
      columns.unshift({
        title: (
          <Checkbox
            noMargin
            onChange={handleSelectAll}
            checked={isSelectedAll}
          />
        ),
        fieldName: 'id',
        sortable: false,
        width: '60px',
        render(data) {
          return (
            <Checkbox
              noMargin
              onChange={() => {
                handleSelectedChange(data);
              }}
              checked={selectedSalesCenter.includes(data)}
            />
          );
        },
      });
    }

    return columns;
  }, [
    trans,
    generateActionDropdown,
    userInformation.permissions,
    handleSelectAll,
    isSelectedAll,
    selectedSalesCenter,
  ]);

  const handleExportList = () => {
    const requestData = {};
    if (listParams.filters.name.trim()) {
      requestData.name = listParams.filters.name.trim();
    }
    if (listParams.filters.bottlerId.length > 0) {
      requestData.bottlerId = listParams.filters.bottlerId;
    }
    if (listParams.filters.description.trim()) {
      requestData.description = listParams.filters.description.trim();
    }
    handleApiResponse(exportSalesCenters(requestData), (response) => {
      const { url } = response.data.data;

      if (url) {
        handleApiResponse(downloadFile(url), (response) => {
          const urlSegment = url.split('/');
          const fileName = urlSegment[urlSegment.length - 1];
          const href = URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        });
      }
    });
  };

  const showFilterPanel = () => {
    TitleModal({
      size: 'normal',
      children: (
        <SalesCenterListFilter
          bottlerSimpleList={bottlerSimpleList}
          listParameters={listParams}
          setListParameters={setListParams}
        />
      ),
    });
  };

  useEffect(() => {
    if (bottlerList.currentPage <= bottlerList.lastPage) {
      handleGetBottlerList(bottlerList.currentPage);
    } else {
      setBottlerSimpleList(
        bottlerList.data.map((bottler) => {
          return { id: bottler.id, text: bottler.name };
        }),
      );
    }
  }, [
    handleGetBottlerList,
    bottlerList.data,
    bottlerList.currentPage,
    bottlerList.lastPage,
  ]);

  useEffect(() => {
    handleGetSalesCenterList();
  }, [handleGetSalesCenterList]);

  useEffect(() => {
    if (
      listData.length !== 0 &&
      selectedSalesCenter.length === listData.length
    ) {
      setIsSelectedAll(true);
    } else {
      setIsSelectedAll(false);
    }
  }, [listData, selectedSalesCenter]);

  return (
    <>
      <Section noPadding>
        <SectionHeader sticky backgroundFill>
          <SectionToolbar>
            <SectionToolbarItem>
              <Heading1>{trans('SalesCenterList')}</Heading1>
            </SectionToolbarItem>
            <SectionToolbarItem>
              {userInformation.permissions.includes('sales_center.delete') &&
                selectedSalesCenter.length > 0 && (
                  <Button
                    secondary
                    onClick={() => handleBatchRemove(selectedSalesCenter)}
                  >
                    <MdDelete />
                    {trans('Remove')}
                  </Button>
                )}
              {userInformation.permissions.includes('sales_center.create') && (
                <Link to='/sales-center/create'>
                  <Button>
                    <FaPlus />
                    {trans('button:Create')}
                  </Button>
                </Link>
              )}

              <Dropdown fixed>
                <Dropdown.Toggle>
                  <Button secondary>
                    <FaFileDownload />
                    {trans('button:Export')}
                  </Button>
                </Dropdown.Toggle>
                <Dropdown.Content>
                  <DropdownItem onClick={handleExportList}>
                    <DropdownButtonOption>
                      {trans('SalesCenterList')}
                    </DropdownButtonOption>
                  </DropdownItem>
                </Dropdown.Content>
              </Dropdown>

              <Button secondary onClick={showFilterPanel}>
                <FaFilter />
                {trans('Filter')}
              </Button>
            </SectionToolbarItem>
          </SectionToolbar>
        </SectionHeader>
        <SectionBody>
          <Table
            columns={tableColumns}
            data={listData}
            currentPage={listParams.page}
            limit={listParams.limit}
            total={total}
            onLimitChange={onLimitChange}
            onPageChange={onPageChange}
            translation={{
              info: trans('table.info'),
              empty: trans('table.empty'),
            }}
            select={{ type: 'multiple', matcher: 'id' }}
            selected={selectedSalesCenter}
          />
        </SectionBody>
      </Section>
    </>
  );
};

const SalesCenterListFilter = ({
  bottlerSimpleList,
  listParameters,
  setListParameters,
  close,
}) => {
  const { t: trans } = useTranslation();
  const [searchData, setSearchData] = useState({
    ...listParameters.filters,
  });

  const handleChangeSearchKeyword = (key, value) => {
    setSearchData({ ...searchData, [key]: value });
  };

  const handleSearch = () => {
    setListParameters((previousParams) => {
      return {
        ...previousParams,
        page: 1,
        filters: searchData,
      };
    });
    close();
  };

  const handleReset = () => {
    setSearchData({
      name: '',
      bottlerId: [],
      description: '',
    });
  };

  return (
    <>
      <TitleModal.Header handleClose={close}>
        {trans('Filter')}
      </TitleModal.Header>
      <TitleModal.Body noPaddingBottom>
        <Grid columns={2}>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='name'>{trans('Name')}</Label>
              <Input
                type='text'
                id='name'
                value={searchData.name}
                onChange={(event) => {
                  handleChangeSearchKeyword('name', event.target.value);
                }}
              />
            </FormItem>
          </Column>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='bottler'>{trans('Bottler')}</Label>
              <Select
                fullWidth
                multiple
                options={bottlerSimpleList}
                selected={searchData.bottlerId}
                searchSeparator=','
                onSelect={(id) => {
                  handleChangeSearchKeyword('bottlerId', [
                    ...searchData.bottlerId,
                    id,
                  ]);
                }}
                onDeselect={(id) => {
                  const ids = [...searchData.bottlerId];
                  const index = ids.indexOf(id);
                  if (index > -1) {
                    ids.splice(index, 1);
                    handleChangeSearchKeyword('bottlerId', ids);
                  }
                }}
              />
            </FormItem>
          </Column>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='description'>{trans('Description')}</Label>
              <Input
                type='text'
                id='description'
                value={searchData.description}
                onChange={(event) => {
                  handleChangeSearchKeyword('description', event.target.value);
                }}
              />
            </FormItem>
          </Column>
        </Grid>
      </TitleModal.Body>
      <TitleModal.Footer>
        <ButtonGroup>
          <Button danger onClick={handleReset}>
            {trans('button:Reset')}
          </Button>
          <Button danger onClick={close}>
            {trans('button:Cancel')}
          </Button>
          <Button success onClick={handleSearch}>
            {trans('button:Search')}
          </Button>
        </ButtonGroup>
      </TitleModal.Footer>
    </>
  );
};

export default SalesCenterList;
